/* eslint-disable replo/no-tailwind-literal-color */
import type { Editor } from "@tiptap/react";
import type { ChatMessage as ChatMessageType } from "schemas/generated/chatMessage";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import { useMessages } from "@/features/react-editor/components/chat/store/ChatStoreContext";
import IconButton from "@replo/design-system/components/button/IconButton";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import {
  CirclePlus,
  MessageCircleDashed,
  Paintbrush,
  Smartphone,
  Sparkles,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { ChatMessage } from "./ChatMessage";
import { TypingIndicator } from "./TypingIndicator";

type MessageGroup = {
  role: "user" | "assistant";
  messages: ChatMessageType[];
  toolMessageId?: string;
};

interface MessageListProps {
  editor: Editor | null;
  isTyping?: boolean;
  sendChatRequest?: ({
    prompt,
    isHidden,
  }: {
    prompt: string;
    isHidden: boolean;
  }) => Promise<void>;
}

const PlaceholderOption = ({
  icon,
  title,
  prompt,
  editor,
}: {
  icon: React.ReactNode;
  title: string;
  prompt: string;
  editor: Editor | null;
}) => {
  return (
    <div
      className="flex flex-col border border-border rounded-lg p-3 hover:bg-muted-soft cursor-pointer select-text relative"
      onClick={() => {
        editor?.commands.setContent(prompt);
      }}
    >
      <div className="mb-2 pointer-events-auto">{icon}</div>
      <div className="text-base font-medium text-muted pointer-events-auto">
        {title}
      </div>
      <div className="text-sm text-placeholder pointer-events-auto">
        "{prompt}"
      </div>
    </div>
  );
};

export function MessageList({
  isTyping = false,
  editor,
  sendChatRequest,
}: MessageListProps) {
  const messages = useMessages();
  const groupedMessages = groupMessages(messages);
  const [searchParams, setSearchParams] = useSearchParams();

  const chatId = searchParams.get("chatId");
  const lastEditedPageId = useFindLastEditedPageFromMessages(messages);

  // NOTE (Bobby, 2025-07-30): Auto-sets pageId URL param when switching chats based on last edited page.
  // Uses ref to track chat:page combinations to prevent overriding manual page switches and handle
  // trpc query timing (lastEditedPageId loads after chatId changes).
  const lastProcessedRef = React.useRef<string | null>(null);

  /**
   * Updates search params to set or remove pageId based on lastEditedPageId
   */
  const updatePageIdParams = React.useCallback(
    (lastEditedPageId: string | null) => {
      setSearchParams((prev) => {
        if (lastEditedPageId && prev.get("pageId") !== lastEditedPageId) {
          // Set pageId if there's a lastEditedPageId
          if (prev.get("pageId") !== lastEditedPageId) {
            const newParams = new URLSearchParams(prev);
            newParams.set("pageId", lastEditedPageId);
            return newParams;
          }
        } else if (
          !lastEditedPageId &&
          messages.length > 1 &&
          prev.has("pageId")
        ) {
          // Remove pageId if there's no lastEditedPageId
          const newParams = new URLSearchParams(prev);
          newParams.delete("pageId");
          return newParams;
        }
        return prev;
      });
    },
    [setSearchParams, messages.length],
  );

  /**
   * Handles the chat page update logic with deduplication
   */
  const handleChatPageUpdate = React.useCallback(
    (chatId: string, lastEditedPageId: string | null) => {
      const currentCombination = createChatPageCombination(
        chatId,
        lastEditedPageId,
      );

      // Only update if we haven't processed this combination before
      if (lastProcessedRef.current !== currentCombination) {
        lastProcessedRef.current = currentCombination;
        updatePageIdParams(lastEditedPageId);
      }
    },
    [updatePageIdParams],
  );

  React.useEffect(() => {
    if (chatId) {
      handleChatPageUpdate(chatId, lastEditedPageId);
    }
  }, [chatId, lastEditedPageId, handleChatPageUpdate]);

  return (
    <div className="space-y-0 flex flex-col h-full min-h-0 mt-5 gap-5">
      {messages.length === 0 && (
        <div className="flex flex-col h-full w-full min-h-0 items-center justify-center relative ">
          <IconButton icon={<Sparkles />} variant="primary" size="lg" />
          <div className="header-h2 mt-4">Perfect your page</div>
          <div className="text-sm text-muted mb-7">
            Some quick actions for you to get started
          </div>
          <div className="grid grid-cols-2 gap-3 relative">
            {placeholderOptions.map((option, index) => (
              <PlaceholderOption key={index} {...option} editor={editor} />
            ))}
          </div>
        </div>
      )}
      {groupedMessages.map((group, index) => {
        return (
          <MessageGroup
            key={index}
            role={group.role}
            messages={group.messages}
            sendChatRequest={sendChatRequest}
            toolMessageId={group.toolMessageId}
          />
        );
      })}
      {isTyping && (
        <div className="flex flex-row items-center justify-start w-full ">
          <img
            src="/images/chat/replo-chat.svg"
            alt="Replo Chat"
            width={24}
            height={24}
          />
          <TypingIndicator />
        </div>
      )}
    </div>
  );
}

const MessageGroup = ({
  role,
  messages,
  sendChatRequest,
  toolMessageId,
}: {
  role: "user" | "assistant";
  messages: ChatMessageType[];
  sendChatRequest?: ({
    prompt,
    isHidden,
  }: {
    prompt: string;
    isHidden: boolean;
  }) => Promise<void>;
  toolMessageId?: string;
}) => {
  return (
    <div className="flex flex-row items-start">
      {role === "assistant" && (
        <img
          src="/images/chat/replo-chat.svg"
          className="mt-3"
          alt="Replo Chat"
          width={24}
          height={24}
        />
      )}
      <div
        className={twMerge(
          "flex flex-col w-full gap-2 min-w-0",
          role === "user" && "items-end ml-8",
        )}
      >
        {messages.map((message, index) => {
          return (
            <ChatMessage
              key={index}
              sendChatRequest={sendChatRequest}
              messageId={message.id}
              toolMessageId={toolMessageId}
              {...message}
            />
          );
        })}
      </div>
    </div>
  );
};

const placeholderOptions = [
  {
    icon: (
      <IconButton
        icon={<MessageCircleDashed className="text-success" size={12} />}
        variant="primary"
        UNSAFE_className="bg-success-soft border border-green-100 hover:bg-success-soft"
        size="sm"
      />
    ),
    title: "Update Copy",
    prompt: "Update the header copy to be more engaging",
  },
  {
    icon: (
      <IconButton
        icon={<Paintbrush className="text-sky-600" size={12} />}
        variant="primary"
        UNSAFE_className="bg-sky-50 border border-sky-100 hover:bg-sky-50"
        size="sm"
      />
    ),
    title: "Restyle",
    prompt: "Update all the buttons on the page to be fully rounded",
  },
  {
    icon: (
      <IconButton
        icon={<CirclePlus className="text-violet-600" size={12} />}
        variant="primary"
        UNSAFE_className="bg-violet-50 border border-violet-100 hover:bg-violet-50"
        size="sm"
      />
    ),
    title: "Insert Components",
    prompt: "Add a CTA button below the hero text",
  },
  {
    icon: (
      <IconButton
        icon={<Smartphone className="text-rose-600" size={12} />}
        variant="primary"
        UNSAFE_className="bg-rose-50 border border-rose-100 hover:bg-rose-50"
        size="sm"
      />
    ),
    title: "Optimize for devices",
    prompt: "Make this responsive for mobile and tablets",
  },
];

/**
 * Groups consecutive chat messages by role for easier rendering.
 *
 * This function transforms a flat array of individual messages into grouped message objects.
 * User messages remain grouped as "user", while both "assistant" and "tool" messages are
 * grouped together under the "assistant" role since they represent the AI's response flow.
 *
 * @param messages - Array of individual chat messages from the database
 * @returns Array of message groups where consecutive messages of the same role type are combined
 *
 * @example
 * Input:
 * [
 *   { role: 'user', content: [] },
 *   { role: 'assistant', content: [] },
 *   { role: 'tool', content: [] },
 *   { role: 'assistant', content: [] }
 * ]
 *
 * Output:
 * [
 *   { role: "user", messages: [{ role: 'user', content: [] }] },
 *   {
 *     role: "assistant",
 *     messages: [
 *       { role: "assistant", content: [] },
 *       { role: "tool", content: [] },
 *       { role: "assistant", content: [] }
 *     ]
 *   }
 * ]
 */
const groupMessages = (messages: ChatMessageType[]): MessageGroup[] => {
  return messages.reduce<MessageGroup[]>((groups, message) => {
    // this makes everything not a user an assistant message (e.g. tool messages).
    const groupRole = message.role === "user" ? "user" : "assistant";
    const isToolMessage = message.role === "tool";
    const lastGroup = groups[groups.length - 1];

    // Check if any content item has isNewGroup flag
    const shouldStartNewGroup = message.content.some(
      (contentItem) =>
        contentItem.type === "text" && contentItem.isNewMessageGroup === true,
    );

    if (lastGroup?.role === groupRole && !shouldStartNewGroup) {
      lastGroup.messages.push(message);
      if (isToolMessage && !lastGroup.toolMessageId) {
        lastGroup.toolMessageId = message.id;
      }
    } else {
      groups.push({
        role: groupRole,
        messages: [message],
        ...(isToolMessage && { toolMessageId: message.id }),
      });
    }

    return groups;
  }, []);
};

// This function is used to look through chat messages and find which page to show in the editor (if any). We take the first of the following:
//  - Last edited page
//  - Last created page from a selected template
const useFindLastEditedPageFromMessages = (
  messages: ChatMessageType[],
): string | null => {
  const projectId = useCurrentProjectId();

  // Find the first message with either fileVersionId or selectedTemplate fileId
  const result = React.useMemo(() => {
    for (const message of messages) {
      // Check for tool message with fileVersionId
      if (message.role === "tool") {
        for (const content of message.content) {
          if (content.type === "tool-result" && content.result.fileVersionId) {
            return { type: "fileVersion", id: content.result.fileVersionId };
          }
        }
      }

      // Check for user message with selectedTemplate
      if (message.role === "user") {
        for (const content of message.content) {
          if (
            content.type === "text" &&
            content.contentType === "selectedTemplate"
          ) {
            try {
              const parsed = JSON.parse(content.text);
              if (parsed.fileId) {
                return { type: "fileId", id: parsed.fileId };
              }
            } catch {
              continue;
            }
          }
        }
      }
    }
    return null;
  }, [messages]);

  // Fetch the file version using trpc if we found a fileVersionId
  const { data: fileVersion } = trpc.fileVersion.get.useQuery(
    result?.type === "fileVersion" && projectId
      ? {
          id: result.id,
          projectId,
          shouldIncludeDeleted: true,
        }
      : skipToken,
  );

  // Return fileId directly if selectedTemplate, or from fileVersion if tool message
  if (result?.type === "fileId") {
    return result.id;
  }
  return fileVersion?.fileId ?? null;
};

const createChatPageCombination = (chatId: string, pageId: string | null) => {
  return `${chatId}:${pageId ?? "none"}`;
};
